import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import "../../styles/scss/MultiImageUpload.scss";

const MultiImageUpload = ({
  apiUrl,
  payload = {},
  existingImages = [],
  onUploadSuccess,
  deleteApiUrl,
  deleteApiRefetch,
  uploadHandler,
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (existingImages.length) {
      const formatted = existingImages.map((img) => ({
        preview: img?.fileUrl,
        id: img?.id,
        uploaded: true,
      }));
      setImages(formatted);
    }
  }, [existingImages]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    const previews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      uploaded: false,
    }));

    setImages((prev) => [...prev, ...previews]);

    uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    setLoading(true);

    if (uploadHandler) {
      for (const file of files) {
        try {
          await uploadHandler(file);
        } catch (error) {
          console.error(error);
        }
      }
      onUploadSuccess?.();
    } else {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("files", file);
      });

      Object.keys(payload).forEach((key) => {
        formData.append(key, payload[key]);
      });

      try {
        const response = await request.post(apiUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        onUploadSuccess?.(response.data);
      } catch (error) {
        console.error(error);
      }
    }

    setLoading(false);
  };

  const removeImage = async (fileId) => {
    try {
      const response = await request.delete(deleteApiUrl + "/" + fileId);
      if (response?.data) {
        toast.success("File deleted successfully");
        deleteApiRefetch?.();
        setImages((prev) => prev.filter((img) => img.id !== fileId));
      }
    } catch (error) {
      console.error(error);
    }
  };


  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        const previews = imageFiles.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
          uploaded: false,
        }));
        setImages((prev) => [...prev, ...previews]);
        uploadFiles(imageFiles);
      }
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box className="multi-image-upload">
      <Box
        className={`upload-zone ${isDragActive ? "drag-active" : ""}`}
        onClick={handleBrowse}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <Box className="upload-zone-icon">
          <span className="material-symbols-outlined">cloud_upload</span>
        </Box>

        <Box className="upload-zone-text">
          <p>Drag and drop images here, or</p>
          <p>
            <span className="upload-link">browse</span> to choose files
          </p>
          <p style={{ marginTop: "8px", opacity: 0.7 }}>Supports JPG, PNG up to 10MB</p>
        </Box>
      </Box>

      {loading && (
        <Box className="upload-loading">
          <Box className="spinner"></Box>
        </Box>
      )}

      {images.length > 0 && (
        <Box className="image-gallery-grid">
          {images.map((img, index) => (
            <Box
              key={index}
              className={`image-item ${index === 0 ? "featured" : ""}`}
            >
              <img
                src={img.preview}
                alt={`Preview ${index + 1}`}
                className="image-preview"
              />
              
              <Box className="image-overlay">
                <button
                  className="image-action-btn delete"
                  onClick={() => removeImage(img?.id)}
                  title="Remove"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MultiImageUpload;
